'use client';

import type { ComponentProps, ElementType, ReactNode } from 'react';

import { tv } from '../../lib/tv';
import { Input } from '../ui/input';
import { useFieldControl } from './field';

export type TextInputProps = Omit<ComponentProps<'input'>, 'size'> & {
  /** Figma "Size": Small 32 · Medium 40 (default) · Large 48. */
  size?: 'sm' | 'md' | 'lg';
  /** Icon before the text, e.g. MagnifyingGlass. */
  leadingIcon?: ElementType<{ className?: string }>;
  /** Icon after the text. */
  trailingIcon?: ElementType<{ className?: string }>;
  /**
   * An interactive control after the text, e.g. a password reveal
   * `IconButton` - where Figma draws the Trailing icon. Rendered after
   * `trailingIcon`; give it an accessible name.
   */
  trailingAction?: ReactNode;
  /** Class for the outer box (width, margins). */
  className?: string;
};

/** The Text Input box, shared with Search Field and Combobox (Figma instances it there). */
export const textInputStyles = tv({
  slots: {
    root: [
      'group/text-input flex w-full items-center gap-2 border border-border-strong bg-bg-surface text-text-primary',
      'transition-[border-color,box-shadow] duration-[120ms] ease-out',
      'hover:border-border-strong-hover',
      // Focus: 1.5px border/focus stroke (1px border + 0.5px inset ring, so
      // nothing shifts) + Focus/Input halo.
      'has-[input:focus-visible]:border-border-focus has-[input:focus-visible]:shadow-focus-input',
      'has-[input:focus-visible]:inset-ring-[0.5px] has-[input:focus-visible]:inset-ring-border-focus',
      // Error: 1.5px status/danger/fg stroke. The message lives in Field.
      'has-[input[aria-invalid=true]]:border-status-danger-fg',
      'has-[input[aria-invalid=true]]:inset-ring-[0.5px] has-[input[aria-invalid=true]]:inset-ring-status-danger-fg',
      'has-[input:disabled]:border-border-default has-[input:disabled]:bg-bg-disabled has-[input:disabled]:text-text-disabled',
    ],
    leading:
      'text-icon-secondary shrink-0 group-has-[input:disabled]/text-input:text-icon-tertiary',
    trailing: 'text-icon-tertiary shrink-0',
  },
  variants: {
    size: {
      sm: {
        root: 'text-body-sm h-8 rounded-md px-3',
        leading: 'size-4',
        trailing: 'size-4',
      },
      md: {
        root: 'text-body-md h-10 rounded-md px-3',
        leading: 'size-[18px]',
        trailing: 'size-[18px]',
      },
      lg: {
        root: 'text-body-lg h-12 rounded-lg px-4',
        leading: 'size-5',
        trailing: 'size-5',
      },
    },
  },
  defaultVariants: { size: 'md' },
});

/**
 * Single-line text entry (Figma: Components ↳ Inputs ↳ Text Input). Pair
 * with Field for the label and helper text; inside a Field it picks up id,
 * description, invalid and disabled automatically.
 */
export function TextInput({
  size,
  leadingIcon: LeadingIcon,
  trailingIcon: TrailingIcon,
  trailingAction,
  className,
  ...props
}: TextInputProps) {
  const styles = textInputStyles({ size });
  const inputProps = useFieldControl(props);
  return (
    <div data-slot="text-input" className={styles.root({ className })}>
      {LeadingIcon && <LeadingIcon aria-hidden className={styles.leading()} />}
      <Input {...inputProps} />
      {TrailingIcon && <TrailingIcon aria-hidden className={styles.trailing()} />}
      {trailingAction}
    </div>
  );
}
